<template>
  <TableList
    v-rect-loading="loading"
    class="verify-upload-location-page"
    row-key="id"
    :showCreateBtn="false"
    :title="$t(`${i18nPrefix}.label.uploadLocation`)"
    :tableColumns="tableColumns"
    :listData="locationList"
    :actionBtn="actionBtn"
    :cellStyle="cellStyle"
  >
    <template #btn-before>
      <span class="tip">{{ $t(`${i18nPrefix}.message.uploadSuccess`, { totalCount: totalCount, failedCount: failedCount}) }}</span>
    </template>
    <template #address="{data, row}">
      <el-popover trigger="hover" placement="right-start" :disabled="!row.lat || !row.lng" @show="handleShowMap(row)">
        <template #reference>
          <span>{{ data }}</span>
        </template>
        <div v-if="showMapRow === row" ref="mapPop"></div>
      </el-popover>
    </template>
    <template #page-bottom>
      <LocationEditorDialog
        class="editor-dialog"
        :show.sync="locationDialog.show"
        :value="locationDialog.location"
        :titleI18nKey="`${i18nPrefix}.label.editLocation`"
        :dialogLoading="locationDialog.loading"
        @change="handleEditLocationChanged"
      />
      <DialogWrapper
        :show.sync="removeDialog.show"
        :title="removeDialog.title"
        @save="confirmRemoveLocation"
      >
      </DialogWrapper>
      <div class="btn-container">
        <el-button class="cancel" @click="handelCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save" type="primary" @click="handelSave">{{ $t('common.btn.save') }}</el-button>
      </div>
      <div ref="map" class="map-container" :id="map.container"></div>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import LocationEditorDialog from '@/components/job/editor/locationEditorDialog.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { addI18nProperty } from '@/i18n'
import { getAMap } from '@/plugins/amap'
import { mapActions } from 'vuex'

export default {
  components: {
    TableList,
    DialogWrapper,
    LocationEditorDialog
  },
  props: {
  },
  data () {
    return {
      i18nPrefix: 'admin.company.jobLocationManagement',
      loading: false,
      locationList: [],
      locationDialog: {
        loading: false,
        location: null,
        show: false
      },
      removeDialog: {
        show: false,
        title: null,
        rowIndex: null
      },
      totalCount: 0,
      failedCount: 0,
      map: {
        container: 'mapContainer',
        hasInit: false,
        instance: null,
        geoCoder: null,
        marker: null,
        toolbar: null
      },
      cityList: null,
      showMapRow: null,
      mapLoader: null,
      getLocationQueue: null,
      lastGetLocationAt: 0,
      getLocationInterval: 500
    }
  },
  computed: {
    actionBtn () {
      return {
        edit: {
          show: true,
          labelI18nKey: 'common.btn.edit',
          onclick: this.editLocation
        },
        remove: {
          show: true,
          labelI18nKey: `${this.i18nPrefix}.btn.remove`,
          onclick: this.removeLocation
        }
      }
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.city`),
          dataKey: 'city',
          showOverflowTooltip: true,
          formatter (data) {
            return data.i18nName || data.name || null
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.region`),
          dataKey: 'region.i18nName',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.cbd`),
          dataKey: 'cbd.i18nName',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.address`),
          dataKey: 'address',
          slotName: 'address',
          showOverflowTooltip: true
        }
      ]
      return columns
    },
    verifyLocation () {
      return _.every(this.locationList, location => !this.isLocationFailed(location))
    },
    queryCompany () {
      return this.$route.query.company
    },
    mapEl () {
      return this.$refs.map
    }
  },
  created () {
    const uploadedLocationInfo = this.$route.query.uploaded_info
    this.locationList = _.map(uploadedLocationInfo.location_list, location => {
      return {
        ...location,
        failed: false
      }
    })
    this.totalCount = uploadedLocationInfo.total_num
    this.failedCount = uploadedLocationInfo.failed_num
    this.initData()
  },
  mounted () {
    this.initMap()
  },
  methods: {
    ...mapActions('worldLocation', ['getCommonCityList', 'getCityRegionList', 'getCityRegionCbdList']),
    ...mapActions('company', ['createCompanyJobLocation']),
    async initData () {
      this.loading = true
      try {
        await this.loadCityList()
        await this.parseLocationList(this.locationList)
      } finally {
        this.loading = false
      }
    },
    async parseLocationList (locationList) {
      const promiseList = _.map(locationList, async location => {
        if (location.city?.id) {
          addI18nProperty(location.city, 'i18nName', 'cn_name', 'name')
          const success = await this.initUploadedLocation(location)
          location.failed = !success
        } else {
          location.failed = true
        }
      })
      await Promise.all(promiseList)
    },
    async loadAMap () {
      if (!this.mapLoader) {
        const plugins = ['AMap.Geocoder', 'AMap.PlaceSearch', 'AMap.ToolBar']
        this.mapLoader = getAMap(plugins, '2.0', false)
      }
      return this.mapLoader
    },
    async initMap () {
      const AMap = await this.loadAMap()
      const markerIcon = new AMap.Icon({
        image: require('@/assets/amap/poi-marker-red.png'),
        imageSize: new AMap.Size(25, 34)
      })
      const marker = new AMap.Marker({
        icon: markerIcon,
        offset: new AMap.Pixel(-13, -30),
        draggable: true,
        cursor: 'move'
      })
      this.map.geoCoder = new AMap.Geocoder()
      this.map.marker = marker
      this.map.toolbar = new AMap.ToolBar()
      this.map.hasInit = true
      if (!this.map.instance) {
        const mapInstance = new AMap.Map(this.map.container, { zoom: 15 })
        mapInstance.addControl(this.map.toolbar)
        mapInstance.on('click', (event) => {
          const lngLat = event.lnglat
          this.setMapMarkerPosition(lngLat)
        })
        this.map.instance = mapInstance
      }
    },
    editLocation (row, index) {
      this.locationDialog.rowIndex = index
      if (!row.city || !row.city.id) {
        this.locationDialog.location = {
          address: row.address
        }
      } else {
        this.locationDialog.location = row
      }
      this.locationDialog.show = true
    },
    removeLocation (row, index) {
      this.removeDialog.rowIndex = index
      const cityName = row.city.i18nName || row.city.name
      this.removeDialog.title = this.$t(`${this.i18nPrefix}.message.removeLocation`, { cityName: cityName || row.name, address: row.address })
      this.removeDialog.show = true
    },
    confirmRemoveLocation () {
      this.locationList.splice(this.removeDialog.rowIndex, 1)
      this.removeDialog.show = false
    },
    handleEditLocationChanged (location) {
      location.failed = this.isLocationIncomplete(location)
      this.locationList.splice(this.locationDialog.rowIndex, 1, location)
      this.locationDialog.show = false
    },
    async handelSave () {
      if (!this.verifyLocation) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.notAllMatchedGEO`))
        return
      }
      const locationList = _.map(this.locationList, location => {
        return {
          city_id: location.city.id,
          region_id: location.region?.id,
          cbd_id: location.cbd?.id,
          address: location.address,
          lng: location.lng,
          lat: location.lat
        }
      })
      const data = {
        company_id: this.queryCompany,
        job_location_list: locationList
      }
      this.loading = true
      try {
        const resp = await this.createCompanyJobLocation(data)
        if (resp) {
          this.$router.push({
            path: '/v-admin/job-location/management/list',
            query: {
              company: this.queryCompany
            }
          })
          this.$message.success(this.$t('common.message.saveSuccess'))
        }
      } finally {
        this.loading = false
      }
    },
    handelCancel () {
      this.$router.go(-1)
    },
    async initUploadedLocation (location) {
      const province = (location.city?.parent.cn_name || '').trim()
      const city = (location.city?.cn_name || '').trim()
      const region = (location.region?.cn_name || '').trim()
      let address = (location.address || '').trim()
      let formattedAddress = ''
      if (province) {
        formattedAddress += province
        if (address.startsWith(province)) {
          address = address.substring(province.length)
        }
      }
      if (city && city !== province) {
        formattedAddress += city
        if (address.startsWith(city)) {
          address = address.substring(city.length)
        }
      }
      if (region) {
        formattedAddress += region
        if (address.startsWith(region)) {
          address = address.substring(region.length)
        }
      }
      formattedAddress += address
      if (!formattedAddress) {
        return false
      }
      if (!this.map.instance) {
        await this.initMap()
      }
      const locationInfo = await this.getLngLatByAddress(province, formattedAddress)
      if (!locationInfo) {
        return false
      }
      const lngLat = locationInfo.location
      const info = await this.getAddressByLngLat(lngLat)
      if (info) {
        await this.convertAddressComponent(info.addressComponent, info.formattedAddress, location)
      }
      this.$set(location, 'lng', lngLat.lng)
      this.$set(location, 'lat', lngLat.lat)
      location.failed = false
      return true
    },
    getLngLatByAddress (province, formattedAddress) {
      return this.runGetLocationWithRateLimit(() => {
        if (province) {
          let searchProvince = province
          if (searchProvince.startsWith('香港')) {
            searchProvince = '香港特别行政区'
          } else if (searchProvince.startsWith('澳门')) {
            searchProvince = '澳门特别行政区'
          } else if (searchProvince.startsWith('台湾')) {
            searchProvince = '台湾省'
          }
          this.map.geoCoder.setCity(searchProvince)
        } else {
          this.map.geoCoder.setCity()
        }
        return new Promise(resolve => {
          const timeoutError = setTimeout(() => {
            resolve(null)
          }, 5000)
          this.map.geoCoder.getLocation(formattedAddress, (status, result) => {
            clearTimeout(timeoutError)
            if (status !== 'complete' || !result || result.info !== 'OK' || _.isEmpty(result.geocodes)) {
              resolve(null)
              return
            }
            resolve(result.geocodes[0])
          })
        })
      })
    },
    getAddressByLngLat (lngLat) {
      return new Promise(resolve => {
        const timeoutError = setTimeout(() => {
          resolve(null)
        }, 5000)
        this.map.geoCoder.getAddress(lngLat, (status, result) => {
          clearTimeout(timeoutError)
          if (status !== 'complete' || !result || result.info !== 'OK') {
            resolve(null)
            return
          }
          resolve(result.regeocode)
        })
      })
    },
    async runGetLocationWithRateLimit (request) {
      let requestPromise = null
      const run = async () => {
        await this.waitGetLocationSlot()
        requestPromise = request()
      }
      const currentQueue = this.getLocationQueue || Promise.resolve()
      const nextQueue = currentQueue.then(run, run)
      this.getLocationQueue = nextQueue.catch(() => {})
      await nextQueue
      return requestPromise
    },
    async waitGetLocationSlot () {
      const waitTime = this.lastGetLocationAt + this.getLocationInterval - Date.now()
      if (waitTime > 0) {
        await new Promise(resolve => setTimeout(resolve, waitTime))
      }
      this.lastGetLocationAt = Date.now()
    },
    async convertAddressComponent (addressComponent, formattedAddress, location) {
      if (!addressComponent) {
        return
      }
      // const isTaiWan = false
      let province = addressComponent.province
      let city = addressComponent.city
      let region = addressComponent.district
      if (!city || city.length < 1) {
        if (province.endsWith('市')) {
          city = province
        } else if (region.endsWith('市')) {
          city = region
          region = addressComponent.township
        }
      }
      const cbdList = addressComponent.businessAreas
      // const street = addressComponent.street
      // const streetNumber = addressComponent.streetNumber
      if (province.startsWith('澳门')) {
        province = '澳门'
      } else if (province.startsWith('香港')) {
        province = '香港'
      } else if (province.startsWith('台湾')) {
        province = '台湾'
        city = city || location.city?.cn_name
        // isTaiWan = true
      } else if (province === '新疆维吾尔自治区') {
        province = '新疆自治区'
      } else if (province === '宁夏回族自治区') {
        province = '宁夏自治区'
      } else if (province === '广西壮族自治区') {
        province = '广西自治区'
      }

      const cityInfo = _.find(this.cityList, cityInfoItem => {
        let cityName = city
        if (cityName.endsWith('市')) {
          cityName = cityName.substring(0, cityName.indexOf('市'))
        }
        return (cityInfoItem.cn_name === city || cityInfoItem.cn_name === cityName) && cityInfoItem.parent.cn_name === province
      })
      if (cityInfo && location.city?.id !== cityInfo.id) {
        this.$set(location, 'city', cityInfo)
      }
      if (location.city?.id) {
        const regionListInfo = await this.loadRegionOrCbd(location)
        const regionInfo = _.find(regionListInfo, { cn_name: region })
        if (regionInfo) {
          this.$set(location, 'region', regionInfo)
        }
      }
      if (location.region?.id) {
        const cbdListInfo = await this.loadRegionOrCbd(location, 'cbd')
        _.forEach(cbdList, cbd => {
          const cbdInfo = _.find(cbdListInfo, { cn_name: cbd.name })
          if (cbdInfo) {
            this.$set(location, 'cbd', cbdInfo)
          }
          return !cbdInfo
        })
      }

      // if (isTaiWan) {
      //   return
      // }
      // let address = null
      // if (region && street) {
      //   address = `${region}${street}${streetNumber}`
      // } else {
      //   address = formattedAddress
      //   const originProvince = addressComponent.province
      //   if (originProvince && address.startsWith(originProvince)) {
      //     address = address.substring(originProvince.length)
      //   }
      //   const selectCityName = location.city?.cn_name
      //   if (city && address.startsWith(city)) {
      //     address = address.substring(city.length)
      //   } else if (!city && address.startsWith(region)) {
      //     address = address.substring(region.length)
      //   }
      //   if (selectCityName && address.startsWith(selectCityName)) {
      //     address = address.substring(selectCityName.length)
      //   }
      // }
      // this.$set(location, 'address', address)
    },
    async loadCityList () {
      const params = {
        need_parent: 1
      }
      const resp = await this.getCommonCityList(params)
      this.cityList = _.chain(resp)
        .map(city => {
          this.addI18nName(city.parent)
          this.addI18nName(city)
          return city
        })
        .sortBy('parent.pinyin_name')
        .value()
    },
    async loadRegionOrCbd (location, name) {
      let func = this.getCityRegionList
      let params = { cityId: location.city.id }
      if (name === 'cbd') {
        func = this.getCityRegionCbdList
        params = { cityId: location.city.id, regionId: location.region.id }
      }
      const resp = await func(params)
      return _.map(resp, this.addI18nName)
    },
    async handleShowMap (location) {
      if (location.lng && location.lat) {
        this.showMapRow = location
        await this.$nextTick()
        const mapPopEl = this.$refs.mapPop
        if (this.mapEl) {
          this.mapEl.style.right = '0'
          mapPopEl.appendChild(this.mapEl)
        }
        await this.renderMap(location)
      }
    },
    async renderMap (location) {
      this.setMapMarkerPosition([location.lng, location.lat])
    },
    setMapMarkerPosition (lngLat) {
      const map = this.map
      const mapInstance = map.instance
      const marker = map.marker
      if (!marker.getMap()) {
        marker.setMap(mapInstance)
      }
      mapInstance.setCenter(lngLat)
      marker.setPosition(lngLat)
    },
    addI18nName (obj) {
      return addI18nProperty(obj, 'i18nName', 'cn_name', 'name')
    },
    isLocationIncomplete (location) {
      return !location.city?.id || !location.lat || !location.lng
    },
    isLocationFailed (location) {
      return Boolean(location.failed || this.isLocationIncomplete(location))
    },
    cellStyle ({ row, columnIndex }) {
      if (columnIndex === 0 && this.isLocationFailed(row)) {
        return {
          color: '#f00'
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.verify-upload-location-page {
  .btn-container {
    text-align: right;
    margin-top: 20px;
    margin-right: 50px;

    .save {
      margin-left: 50px;
    }
  }
}

.map-container {
  right: 1000px;
  width: 200px;
  height: 200px;
}
</style>
